import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { obterFeedbacksPainel, responderFeedbackPainel } from "../services/api";

/**
 * 💬 O QUE OS CANDIDATOS CONTARAM — a aba que faltava no painel da empresa (20/09/2026).
 *
 * Nasceu de uma pergunta do Nelson ("a empresa visualiza os feedbacks, correto? e pode dar uma resposta
 * nele"): não existia tela nenhuma. E isso pesava, porque no mesmo dia o feedback passou a contar na
 * reputação — a empresa perdia ponto por uma resposta que não via nem podia contestar.
 *
 * 📋 AGRUPADO POR VAGA, não por pessoa (pedido dele: "separe por vaga, qtde e opção de abrir"). Quem
 *    recruta pensa em PROCESSO: "como foi a vaga de Analista?" é a pergunta real; "o que o candidato 12
 *    achou" é ruído até que se saiba de qual processo ele veio. E agrupado, o padrão aparece — cinco
 *    pessoas dizendo o mesmo sobre a MESMA vaga é informação; espalhadas numa lista, viram cinco queixas.
 *
 * 🔒 SEM NOME DE QUEM RESPONDEU. O feedback foi dado ao eaiRH com essa promessa. Se contar como foi tiver
 *    custo pessoal, quem procura emprego não conta — e aí não há o que mostrar para a empresa nenhuma.
 */
const ETAPA = {
  Aguardando: "⏳ Aguardando", SemRetorno: "🔇 Sem retorno", ChamadoParaEntrevista: "📅 Chamado p/ entrevista",
  FezEntrevista: "🗣️ Fez entrevista", RecebeuNao: "✋ Recebeu um não", Contratado: "🎉 Contratado",
};

export default function FeedbacksEmpresa() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [aberta, setAberta] = useState(null);      // vagaId expandida
  const [respondendo, setRespondendo] = useState(null);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => { obterFeedbacksPainel().then(setDados).catch((e) => setErro(e.message)); }, []);

  const dia = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("pt-BR") : "—"; } catch { return "—"; } };
  const semRetorno = (f) => f.houveRetorno === false || f.retornoDeQuem === "eairh";

  async function enviar(f) {
    setEnviando(true); setErro("");
    try {
      const r = await responderFeedbackPainel(f.id, texto);
      setDados((d) => ({
        ...d,
        semResposta: Math.max(0, d.semResposta - 1),
        itens: d.itens.map((x) => (x.id === f.id ? { ...x, respostaDaEmpresa: texto, respostaEm: r.respostaEm } : x)),
      }));
      setRespondendo(null); setTexto("");
    } catch (e) { setErro(e.message); } finally { setEnviando(false); }
  }

  if (erro && !dados) return <p role="alert" className="text-sm text-red-600">{erro}</p>;
  if (!dados) return <p className="text-gray-500">Carregando...</p>;

  // Agrupa por vaga, mantendo a ordem de quem teve resposta mais recente.
  const porVaga = [];
  for (const f of dados.itens) {
    let g = porVaga.find((x) => x.vagaId === f.vagaId);
    if (!g) { g = { vagaId: f.vagaId, vaga: f.vaga, itens: [] }; porVaga.push(g); }
    g.itens.push(f);
  }
  for (const g of porVaga) {
    g.semRetorno = g.itens.filter(semRetorno).length;
    g.semResposta = g.itens.filter((f) => !f.respostaDaEmpresa).length;
    const notas = g.itens.filter((f) => f.nota != null).map((f) => f.nota);
    g.nota = notas.length ? (notas.reduce((a, b) => a + b, 0) / notas.length).toFixed(1) : null;
  }
  porVaga.sort((a, b) => b.semRetorno - a.semRetorno || b.itens.length - a.itens.length);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card v={dados.total} l="pessoas contaram como foi" cor="text-gray-800" />
        <Card v={dados.semRetorno} l="disseram que não tiveram retorno" cor="text-amber-600" destaque={dados.semRetorno > 0} />
        <Card v={porVaga.length} l="vagas com feedback" cor="text-gray-700" />
        <Card v={dados.notaMedia ?? "—"} l="nota média que deram" cor="text-blue-600" />
      </div>

      {dados.total === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-600">
          <p className="font-semibold text-gray-900">Ninguém contou como foi ainda.</p>
          <p className="mt-1 text-sm">
            Quando um processo termina, perguntamos ao candidato como foi. O que ele responder aparece aqui —
            e é o que forma a reputação da sua empresa no eaiRH.
          </p>
        </div>
      ) : (<>
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Você vê <b>o que a pessoa respondeu</b>, nunca o nome dela. Se quiser contar a sua versão, responda:
          <b> quem escreveu recebe a sua resposta</b>.
        </div>

        {erro && <p role="alert" className="mb-3 text-sm text-red-600">{erro}</p>}

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 font-semibold">Vaga</th>
                <th className="px-3 py-2 text-right font-semibold">Feedbacks</th>
                <th className="px-3 py-2 text-right font-semibold">Sem retorno</th>
                <th className="px-3 py-2 text-right font-semibold">Nota</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" style={{ fontVariantNumeric: "tabular-nums" }}>
              {porVaga.map((g) => (
                <Fragment key={g.vagaId}>
                  <tr className={`cursor-pointer hover:bg-gray-50 ${aberta === g.vagaId ? "bg-gray-50" : ""}`}
                    onClick={() => setAberta(aberta === g.vagaId ? null : g.vagaId)}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-gray-900">{g.vaga}</span>
                      {g.semResposta > 0 && (
                        <div className="text-xs text-gray-500">{g.semResposta} sem sua resposta</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold">{g.itens.length}</td>
                    <td className={`px-3 py-2.5 text-right font-semibold ${g.semRetorno > 0 ? "text-amber-700" : "text-gray-400"}`}>
                      {g.semRetorno || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-700">{g.nota ?? "—"}</td>
                    <td className="px-3 py-2.5 text-right text-xs font-semibold text-primary">
                      {aberta === g.vagaId ? "fechar ▴" : "abrir ▾"}
                    </td>
                  </tr>

                  {aberta === g.vagaId && (
                    <tr>
                      <td colSpan={5} className="bg-gray-50 px-4 pb-4 pt-1">
                        <div className="mb-2 text-xs">
                          <Link to={`/vagas/${g.vagaId}`} className="font-semibold text-primary hover:underline">
                            ver a vaga como o candidato vê →
                          </Link>
                        </div>
                        <div className="space-y-2">
                          {g.itens.map((f) => (
                            <div key={f.id}
                              className={`rounded-xl border bg-white p-3 ${semRetorno(f) ? "border-amber-300" : "border-gray-200"}`}>
                              <div className="flex flex-wrap items-center gap-2">
                                {semRetorno(f) && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                                    🔇 não teve retorno
                                  </span>
                                )}
                                {f.nota != null && (
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">nota {f.nota}</span>
                                )}
                                {f.contratado && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800">🎉 contratado</span>}
                                {f.etapa && <span className="text-[11px] text-gray-500">{ETAPA[f.etapa] || f.etapa}</span>}
                                <span className="text-[11px] text-gray-400">
                                  candidatou-se em {dia(f.candidatouEm)} · contou em {dia(f.respondidoEm)}
                                </span>
                              </div>

                              {f.comentario ? (
                                <blockquote className="mt-2 border-l-4 border-gray-200 pl-3 text-sm text-gray-800 whitespace-pre-line">
                                  {f.comentario}
                                </blockquote>
                              ) : (
                                <p className="mt-2 text-sm text-gray-500 italic">Respondeu as perguntas, sem escrever comentário.</p>
                              )}

                              {f.respostaDaEmpresa ? (
                                <div className="mt-2 rounded-lg bg-gray-50 p-2.5">
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    Sua resposta · {dia(f.respostaEm)}
                                  </p>
                                  <p className="mt-1 text-sm text-gray-800 whitespace-pre-line">{f.respostaDaEmpresa}</p>
                                </div>
                              ) : respondendo === f.id ? (
                                <div className="mt-2">
                                  <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} autoFocus
                                    placeholder="Conte a sua versão. Quem escreveu o feedback vai receber isto."
                                    className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-primary focus:outline-none" />
                                  <div className="mt-2 flex gap-2">
                                    <button onClick={() => enviar(f)} disabled={enviando || texto.trim().length < 10}
                                      className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                                      {enviando ? "Enviando..." : "Responder"}
                                    </button>
                                    <button onClick={() => { setRespondendo(null); setTexto(""); }}
                                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-600">Cancelar</button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => { setRespondendo(f.id); setTexto(""); }}
                                  className="mt-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                                  Responder
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </>)}

      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        O que aparece aqui entra na reputação da sua empresa com peso pequeno — uma resposta isolada quase não
        muda o número, e é assim de propósito: num processo com 50 candidatos, 49 são descartados por definição.
        O que move a nota é o padrão, não o caso.
      </p>
    </>
  );
}

function Card({ v, l, cor, destaque }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 text-center ${destaque ? "border-amber-400 ring-1 ring-amber-200" : "border-gray-200"}`}>
      <div className={`text-2xl font-bold ${cor}`} style={{ fontVariantNumeric: "tabular-nums" }}>{v}</div>
      <div className="text-xs text-gray-500">{l}</div>
    </div>
  );
}
